import { ActionType } from '@enums/node-types.enum';
import {
  RollResult,
  ConsumeRoll,
  Prize,
  Collectible,
  MoveBack,
  MoveForward,
  Points,
} from '@models/game-ui-actions';
import { GameConfigService } from '@services/core/GameConfigService';
import { TranslationService } from '@services/core/TranslationService';
import { MovementService } from '@services/game/MovementService';
import ActionMap from './ActionMap';
import GameState from './GameState';

// This service knows HOW to implement each action.
// It keeps the Game scene clean and focused on setup
export class ActionRegistry {
  private trs = new TranslationService();

  constructor(
    private actionMap: ActionMap,
    private gameState: GameState,
    private gameConfig: GameConfigService,
    private movementService: MovementService,
  ) {}

  registerAll(): void {
    this.registerActions();
  }

  private registerActions(): void {
    this.actionMap.register('roll_result', async (payload) => {
      const { position } = payload as RollResult;
      await this.movementService.moveToPosition(Number(position));
    });

    this.actionMap.register('move_back', async (payload) => {
      const { position, steps } = payload as MoveBack;
      const n = Math.abs(steps);
      const finalPos = position + steps;
      const text = n !== 1 ? 'result.moveBackPlural' : 'result.moveBack';
      await this.movementService.animateMoveBack(
        position,
        finalPos,
        this.trs.translateArgs(text, n.toString()),
      );
    });

    this.actionMap.register('move_forward', async (payload) => {
      const { position, steps, finalPosition } = payload as MoveForward;
      if (finalPosition != null) {
        // Two moves - player is already on move_forward tile, now
        // we need to flash it and walk the remaining steps to finalPosition
        await this.movementService.animateMoveForward(
          position,
          finalPosition,
          steps,
        );
      } else {
        const text =
          steps !== 1 ? 'result.moveForwardPlural' : 'result.moveForward';
        this.movementService.flashNode(
          position,
          this.trs.translateArgs(text, steps.toString()),
        );
      }
    });

    this.actionMap.register('consume_roll', async (payload) => {
      const { nextRoll, rollsAvailable } = payload as ConsumeRoll;
      this.gameState.setRollInfo(nextRoll, rollsAvailable);
    });

    this.actionMap.register('finish', async () => {
      this.gameState.completeChapter();
    });

    this.actionMap.register('prize', async (payload) => {
      const data = payload as Prize;
      const action = this.gameConfig.getActionByUuid(data.actionUuid);
      if (!action) return;
      // we don't want to show the popup for collectibles and checkpoints
      if (
        [
          ActionType.Collectible,
          ActionType.Checkpoint,
          ActionType.Finish,
        ].includes(action.actionType)
      )
        return;
      this.movementService.triggerRewardAnimation();
      this.gameState.claimInstantPrize(action);
    });

    this.actionMap.register('checkpoint', async () => {
      const action = this.gameConfig.getActionByNodePosition(
        this.gameState.getPosition(),
      );
      if (!action) return;
      this.gameState.completeMilestone(action);
    });

    this.actionMap.register('skip_turn', async () => {
      const action = this.gameConfig.getActionByNodePosition(
        this.gameState.getPosition(),
      );
      if (!action) return;
      this.gameState.requestPopup(
        this.trs.translate('result.skipTurn'),
        action.uuid,
      );
    });

    this.actionMap.register('extra_roll', async () => {
      const action = this.gameConfig.getActionByNodePosition(
        this.gameState.getPosition(),
      );
      if (!action) return;
      this.gameState.requestPopup(
        this.trs.translate('result.extraRoll'),
        action.uuid,
      );
    });

    this.actionMap.register('collectible', async (payload) => {
      const data = payload as Collectible;
      const action = this.gameConfig.getActionByNodePosition(
        this.gameState.getPosition(),
      );
      if (!action?.collectible) return;
      this.movementService.triggerRewardAnimation();
      this.gameState.claimCollectible(data.quantity, action);
    });

    this.actionMap.register('xp', async (payload) => {
      const data = payload as Points;
      const action = this.gameConfig.getActionByNodePosition(
        this.gameState.getPosition(),
      );
      if (!action || isNaN(+data.value)) return;
      this.gameState.addPoints(Number(data.value), action);
    });
  }
}
